<template>
  <div class="selection">
    <div
      class="selection__row"
      :class="{
        'selection__row--disabled': disabled,
        'selection__row--hovered': hovered,
        'selection__row--telephone': telephoneOnly,
      }"
    >
      <div class="selection__square">
        <IconLogo :opacity="disabled ? '.4' : '1'" />
      </div>
      <div class="selection__right">
        <div class="selection__right__inner">
          <div>
            <p>
              <IconStar
                aria-label="Favorit"
                class="favorite"
                v-if="station.globalStpId === favoriteStation?.id"
              />
              {{
                station.bezeichnung +
                ' &centerdot; ' +
                Math.round(station.distanceFromZipcodeInMeters / 1000) +
                ' km'
              }}
            </p>
            <p>
              {{ station.adresse + ', ' + station.plz + ' ' + station.ort }}
              <span v-if="disabled"> &centerdot; </span>
              <span v-if="!inState" class="not-in-state"> Nicht im gewählten Bundesland </span>
              <span v-if="inState && unbookable" class="unbookable">
                Der Stützpunkt bietet diese Dienstleistung nicht an
              </span>
              <span v-if="inState && keineTermine && !unbookable" class="unbookable">
                Keine Termine verfügbar
              </span>
              <span v-if="telephoneOnly && !keineTermine" class="telephone">
                Terminvereinbarung nur telefonisch
              </span>
            </p>
          </div>
          <slot name="action" />
        </div>
        <template v-if="keineTermine && active">
          <p v-if="keineTermineText" class="selection__right__info">
            {{ keineTermineText }}
          </p>
          <p v-else class="selection__right__info">
            Diese Dienstleistung ist vorübergehend nicht buchbar.
          </p>
        </template>
        <template v-if="!keineTermine && telephoneOnly && active">
          <p v-if="buchungInternetText" class="selection__right__info">
            {{ buchungInternetText }}
          </p>
          <p v-else v-html="telephoneText" class="selection__right__info"></p>
        </template>
        <p v-if="station.anmerkungen" class="selection__right__info">
          {{ station.anmerkungen }}
        </p>
        <hr v-if="divider" />
      </div>
    </div>
  </div>
</template>

<script>
import IconLogo from '@/components/icons/IconLogo.vue'
import IconStar from '@/components/icons/IconStar.vue'
import { mapGetters } from 'vuex'

export default {
  components: { IconLogo, IconStar },
  props: {
    station: {
      type: Object,
    },
    active: {
      type: Boolean,
      default: false,
    },
    divider: {
      type: Boolean,
      default: false,
    },
    hovered: {
      type: Boolean,
      default: false,
    },
  },
  mounted() {
    if (!this.telephoneStationText) this.$store.dispatch('getTelephoneStationText')
  },
  computed: {
    ...mapGetters([
      'telephoneStationIds',
      'telephoneStationText',
      'buchungInternetText',
      'keineTermineStationIds',
      'keineTermineText',
      'unbookableStationIds',
      'favoriteStation',
      'selectedState',
      'landesclubTelephone',
    ]),
    disabled() {
      return !this.inState || this.telephoneOnly || this.keineTermine || this.unbookable
    },
    inState() {
      return this.selectedState.id === this.station.bundeslandId
    },
    /** telephoneOnly is true when InternetBuchung is false */
    telephoneOnly() {
      return this.telephoneStationIds.includes(this.station.dienststelleId)
    },
    telephoneText() {
      return this.telephoneStationText
        .replace('@DIENSTSTELLE_BEZEICHNUNG@', `<span>${this.station.bezeichnung}</span>`)
        .replace('@DIENSTSTELLE_TELEFONNUMMER@', `<span>${this.station.telefon}</span>`)
        .replace('@LANDESCLUB_TELEFON@', `<span>${this.landesclubTelephone}</span>`)
    },
    keineTermine() {
      return this.keineTermineStationIds.includes(this.station.dienststelleId)
    },
    unbookable() {
      return this.unbookableStationIds.includes(this.station.dienststelleId)
    },
  },
}
</script>

<style lang="scss" scoped>
.selection {
  &__row {
    display: flex;
    cursor: pointer;
    margin: 0 -8px;
    padding: 0 8px;

    &:hover:not(&--disabled):not(.expansion) {
      background-color: rgba($color-grey-light, 0.3);
      border-radius: 4px;
    }

    &--hovered {
      background-color: rgba($color-grey-light, 0.3);
      border-radius: 4px;
    }

    &--disabled {
      cursor: default;
      pointer-events: none;

      .selection {
        &__square {
          border-color: $color-grey-dark;
        }
        &__right__inner div p:first-of-type {
          font-weight: $font-weight-regular;
        }
      }

      .el-radio {
        display: none;
      }
    }

    &--telephone {
      .el-radio.is-checked {
        .el-radio__inner {
          background-color: $color-grey-dark;
          border-color: $color-grey-dark;
        }
      }
    }
  }

  &__square {
    @include square-yellow;
    background-color: white;
    margin: 16px 16px 16px 0;
  }

  &__right {
    width: 100%;

    &__inner {
      @include flex-center-align;
      justify-content: space-between;

      div {
        margin: 14px 0;
        width: 100%;

        p:first-of-type {
          display: flex;
          font-weight: $font-weight-bold;

          .favorite {
            height: 22px;
            margin-right: 4px;
          }
        }

        p:last-of-type {
          color: $color-text-secondary;

          span.not-in-state,
          span.unbookable {
            color: $color-danger-primary;
          }

          span.telephone {
            color: $color-warning-primary;
          }
        }
      }
    }

    &__info {
      color: $color-text-secondary;
      margin-bottom: 14px;

      span {
        font-weight: $font-weight-medium;
      }
    }
  }
}
</style>

<!-- 
https://doku.oeamtc.at/pages/viewpage.action?pageId=132843896#KonzeptionTVGAPIf%C3%BCrInternetTerminbuchung-TexteinTVG-Online-GUI

{
  "id": 4609,
  "aktiv": true,
  "bemerkung": "PKW",
  "dauer": 40,
  "schalterBuchungInternet": true,
  "schalterKeineTermineText": "EXAMPLE KEINE TERMINE TEXT",
  "schalterKeineTermine": false,
  "hinweisText": "Example HinweiseText fuer 57a in Wolfsberg",
  "buchungInternetText": "Im Rahmen der gebuchten Dienstleistung überprüfen wir automatisch, ob Ihr Fahrzeug optimal auf die aktuelle Jahreszeit vorbereitet ist (WinterFit, Frühling-Check)"
}

Keine Termine Text
If ( KeineTermine = false ) → No text
If ( KeineTermine = true & KeineTermineText is populated ) → KeineTermineText should be displayed.
If ( KeineTermine = true & KeineTermineText is NULL/"" ) → "Diese Dienstleistung ist vorübergehend nicht buchbar" should be displayed

Buchung Internet Text
If ( BuchungInternet = true ) → buchungInternetText is displayed
If ( BuchungInternet = false ) → "Terminvereinbarung nur telefonisch" (Telephone Text Id: 473)

Stacked Services
KeineTermine has priority over BuchungInternet. Means if BuchungInternet=false and keineTermine=true ONLY keineTermineText or general Text should be displayed.
-->
